import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';
import {
  getArticleCategoriesInMenu,
  getArticlesByCategories,
  getContentHubHomePageData,
  getLatestArticles,
} from 'services/cmsService';
import { IArticleCategorySummary, IArticleSummary, ICategorisedArticles } from 'types/cms';
import { tryToNoticeError } from 'utils/errorInfo';

import ArticleListSection from 'components/CMS/ArticleListSection';
import { FeaturedArticleSection } from 'components/CMS/FeaturedArticleSection';

import { ContentHubContainer } from 'modules/ContentHubContainer';

interface IProps {
  latestArticles: IArticleSummary[];
  articlesWithCategories: ICategorisedArticles[];
  categoriesOnMenu: IArticleCategorySummary[];
  metaTitle: string | undefined;
  metaDescription: string | undefined;
  index: boolean;
}

const Articles = ({
  latestArticles,
  articlesWithCategories,
  categoriesOnMenu,
  metaTitle,
  metaDescription,
  index,
}: IProps) => {
  const ifShowFirstPart = (l, i) => i === 0 || (i === 1 && l > 2);

  return (
    <ContentHubContainer categories={categoriesOnMenu}>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        noindex={!index}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          type: 'article',
        }}
      />
      <FeaturedArticleSection articles={latestArticles} />
      {articlesWithCategories.map(
        (item, index) =>
          ifShowFirstPart(articlesWithCategories.length, index) && (
            <ArticleListSection key={item.name} articleListTitle={item.name} articles={item.data} />
          ),
      )}
      {articlesWithCategories.map(
        (item, index) =>
          !ifShowFirstPart(articlesWithCategories.length, index) && (
            <ArticleListSection key={item.name} articleListTitle={item.name} articles={item.data} />
          ),
      )}
    </ContentHubContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<IProps>> {
  const props: IProps = {
    latestArticles: [],
    articlesWithCategories: [],
    categoriesOnMenu: [],
    metaTitle: undefined,
    metaDescription: undefined,
    index: true,
  };

  try {
    const [latestArticles, categoriesOnMenu, homePageData] = await Promise.all([
      getLatestArticles(),
      getArticleCategoriesInMenu(),
      getContentHubHomePageData(),
    ]);

    const redirect_url = homePageData.redirect_url;
    if (redirect_url) {
      return {
        redirect: {
          destination: redirect_url,
          permanent: true,
        },
      };
    }

    props.latestArticles = latestArticles;
    props.categoriesOnMenu = categoriesOnMenu;
    props.articlesWithCategories = await getArticlesByCategories(categoriesOnMenu);
    props.metaTitle = homePageData?.meta_title ?? null;
    props.metaDescription = homePageData?.meta_description ?? null;
    props.index = homePageData?.index ?? true;

    return { props: props };
  } catch (error) {
    const errorMessage = `Article Landing Page Data Error: ${error.message}`;
    tryToNoticeError(new Error(errorMessage, { cause: error }), { error });
    return { notFound: true, props: props };
  }
}

export default Articles;
